/**
 * @voltxampere/engine — Terminal lookup helpers for stamps
 *
 * Stamps reference component pins by their canonical id (`"pos"`,
 * `"neg"`, `"anode"`, `"cathode"`, `"p1"`, `"p2"`, …) — the same
 * id the components-package definition exposes. SPECS §1.3 only
 * guarantees that `terminals.length === pins.length`; the *order*
 * matches the definition by convention but is not part of the
 * schema. So stamps look up terminals by id rather than by index.
 *
 * `getTerminalNetId` throws on absence with a `ContractViolationError`
 * because by the time a stamp runs, `validateComponentInstance`
 * should already have rejected mis-shapen instances. A missing
 * terminal here means either the validator was bypassed or the
 * components definition and instance disagree — both are bugs the
 * caller should hear about loudly.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
export function getTerminalNetId(component, terminalId) {
    const t = component.terminals.find((x) => x.terminalId === terminalId);
    if (t === undefined) {
        throw new ContractViolationError(`getTerminalNetId: component "${component.id}" of type "${component.type}" has no terminal "${terminalId}"`);
    }
    return t.netId;
}
/**
 * Read a numeric parameter, casting through the {@link ParamValue}
 * union. Stamps know which params they need to be plain numbers
 * (resistance, capacitance, voltage, current); unit-bearing values
 * are unwrapped. Strings/booleans throw.
 */
export function getNumericParam(component, key, fallback) {
    const raw = component.params[key];
    if (raw === undefined) {
        if (fallback !== undefined)
            return fallback;
        throw new ContractViolationError(`getNumericParam: component "${component.id}" missing required param "${key}"`);
    }
    if (typeof raw === "number")
        return raw;
    if (typeof raw === "object" && "value" in raw && typeof raw.value === "number") {
        return raw.value;
    }
    throw new ContractViolationError(`getNumericParam: component "${component.id}" param "${key}" is not numeric (got ${typeof raw})`);
}
//# sourceMappingURL=terminal-utils.js.map